import AdminPredefinedInterview from '@components/AdminPredefinedInterview/AdminPredefinedInterview.component'
import InterviewCard from '@components/InterviewCard/InterviewCard.component'
import { Box, Button, TextareaAutosize, Typography } from '@mui/material'
import { styled } from '@mui/system'
import { createFileRoute, useSearch, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query';
import axios from 'axios';
import { useGeneratedCategoriesStore } from '../../store/categoriesStore';

interface PredefinedInterview {
    id: string;
    name: string;
    description: string;
    difficulty: string;
}

interface Category {
    id: string;
    name: string;
    description: string;
    recommendedNumberOfQuestions: number;
    totalNumberOfQuestions: number;
}

export const Route = createFileRoute(
  '/(admin)/_admin/admin-advanced-interview-generator-step-1',
)({
  component: RouteComponent,
})


const StyledContainer = styled(Box)(({theme}) => ({
  display: 'flex',
  flexDirection: 'column',
  padding: '48px 56px',
  background: '#ffffff',
  borderRadius: '24px',
  // border: '1px solid var(--Light-Gray)',
  boxShadow: '0px 1px 2px 0px #0205111A',
  width: '100%',
  justifyContent: 'center',
  [theme.breakpoints.down('md')]: {
    padding: '24px',
  },
}))

const StyledTextArea = styled(TextareaAutosize)(() => ({
  width: '100%',
  minHeight: '200px',
  padding: '12px',
  borderRadius: '8px',
  border: '1px solid var(--Interviewer-Lightest-Black)',
  fontFamily: 'inherit',
  fontSize: '14px',
  lineHeight: '20px',
  resize: 'vertical',
  '&:focus': {
    outline: 'none',
  },
}))



function RouteComponent() {
    const search = useSearch({ from: '/(admin)/_admin/admin-advanced-interview-generator-step-1' });
    const navigate = useNavigate();
    const techId = (search as any).techId;
    const [jobDescription, setJobDescription] = useState('');
    
    // Use Zustand store for generated categories
    const setGeneratedCategories = useGeneratedCategoriesStore((state) => state.setGeneratedCategories);

    // Fetch predefined interviews data
    const { data: predefinedInterviews, isLoading, error } = useQuery({
        queryKey: ['predefined-interviews', techId],
        queryFn: async () => {
            if (!techId) return [];
            const token = localStorage.getItem('token');
            const response = await axios.get(`https://interviewer.runasp.net/api/interviews/main?technologyId=${techId}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            return response.data as PredefinedInterview[];
        },
        enabled: !!techId,
    });

    // Mutation for generating categories from job description
    const generateCategoriesMutation = useMutation({
        mutationFn: async (jobDescription: string) => {
            const token = localStorage.getItem('token');
            const response = await axios.post(
                `https://interviewer.runasp.net/api/technologies/${techId}/categories/scale`,
                {
                    jobDescription: jobDescription
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            return response.data as Category[];
        },
        onSuccess: (categories) => {
            // Store categories in Zustand store
            setGeneratedCategories(categories);
            // Navigate to step-2
            navigate({
                to: '/admin-advanced-interview-generator-step-2',
                search: {
                    techId: techId
                }
            });
        },
        onError: (error) => {
            console.error('Error generating categories:', error);
        }
    });

    const handleNextClick = () => {
        if (jobDescription.trim()) {
            generateCategoriesMutation.mutate(jobDescription);
        }
    };

    return (
        <Box sx={{maxWidth: '80%', mx: 'auto'}}>
            <Box sx={{display: 'flex', flexDirection: 'column', gap: '12px', pt: '64px', maxWidth: '80%', mx: 'auto'}}>
                <Typography variant="h1" sx={{ textAlign: 'center'}}>
                    Tell us about the job position you are creating interview for
                </Typography>
                <Typography sx={{textAlign: 'center', maxWidth: '80%', mx: 'auto', color: 'var(--Interviewer-Lighter-Black)', fontWeight: '600', fontSize: '16px', lineHeight: '24px'}}>
                    It is a long established fact that a reader will be distracted by the readable content of a page when looking at its layout. The point of using Lorem Ipsum is that it has a more-or-less normal distribution of letters.
                </Typography>
            </Box>

            <StyledContainer>
                <Typography variant="h3" sx={{ pb: '8px', fontSize: '16px' }}>
                    Job description
                </Typography>
                <StyledTextArea 
                    value={jobDescription} 
                    onChange={(e) => setJobDescription(e.target.value)}
                    placeholder="Describe the job you're creating an interview for..." 
                />
                <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    sx={{ mt: '32px' }}
                    onClick={handleNextClick}
                    disabled={generateCategoriesMutation.isPending || !jobDescription.trim()}
                >
                    {generateCategoriesMutation.isPending ? 'Generating categories...' : 'Next'}
                </Button>
            </StyledContainer>

            <Box sx={{ py: '56px', width: '100%', px: {xs:'24px', md:'56px'} }}>
                <Typography variant="h4" sx={{ pb: '12px', fontWeight: '600', color: 'var(--Interviewer-Lightest-Black)' }}>In a rush? We got you</Typography>
                <Typography variant="h2" sx={{ pb: '32px' }}>Try our predefined job interviews</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {isLoading && <Typography>Loading predefined interviews...</Typography>}
                    {error && <Typography color="error">Error loading predefined interviews</Typography>}
                    {predefinedInterviews?.map((interview) => (
                        <AdminPredefinedInterview key={interview.id} interview={interview} />
                    ))}
                </Box>
            </Box>
        </Box>
    )
}
